import { useMemo } from "react";

import Box from "ds/components/Box";
import Typography from "ds/components/Typography";
import CollapsibleSection from "ds/components/CollapsibleSection";
import { useToggle } from "hooks/useToggle";
import { UserGuideChapterEnriched } from "shared/UserGuides/types";

import styles from "./styles.module.css";
import UserGuideGroupGuideCard from "./GuideCard";

type UserGuideGroupChapterSectionProps = {
  chapter: UserGuideChapterEnriched;
  groupId?: string;
  initiallyCollapsed?: boolean;
  initiallyExpandedGuideId?: string;
};

const UserGuideGroupChapterSection = ({
  chapter,
  groupId,
  initiallyCollapsed = false,
  initiallyExpandedGuideId,
}: UserGuideGroupChapterSectionProps) => {
  const [isCollapsed, handleToggle] = useToggle(initiallyCollapsed);

  const completedCount = useMemo(
    () => chapter.guides.filter((g) => g.isCompleted).length,
    [chapter.guides]
  );

  return (
    <CollapsibleSection
      title={
        <Box direction="column" gap="small">
          <Typography tag="h3" variant="p-t6">
            {chapter.name}
          </Typography>
          <Typography color="secondary" tag="p" variant="p-body3">
            {chapter.description}
          </Typography>
        </Box>
      }
      isCollapsed={isCollapsed}
      onToggle={handleToggle}
      headerSuffix={
        <Box shrink="0">
          <Typography color="secondary" variant="p-body3" tag="span">
            {completedCount}/{chapter.guides.length} complete
          </Typography>
        </Box>
      }
      ariaLevel={3}
      ariaLabel={chapter.name}
      contentGap="medium"
      className={styles.chapterSection}
    >
      <Box direction="column" gap="medium">
        {chapter.guides.map((guide) => (
          <UserGuideGroupGuideCard
            key={guide.id}
            guide={guide}
            groupId={groupId}
            initiallyCollapsed={initiallyExpandedGuideId !== guide.id}
          />
        ))}
      </Box>
    </CollapsibleSection>
  );
};

export default UserGuideGroupChapterSection;
